import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createContentLayout, getContentLayouts } from '../api/layoutApi';
import { compatibleFormat, versionBox } from '../../../utils/privateLayouts';

export function usePageLayoutTemplates({
  activeWorkflowTab, bookId, pageId, canvasSize, pageLanguage, pageTextEntries, textItemsRef,
  getCurrentTextValues, setPageData, setTextItems, setPageTextEntries,
  setSelectedTextItemId, setSelectedAdditionalEntryId, notify,
}) {
  const currentPageId = useRef(pageId);
  currentPageId.current = pageId;
  const entriesRef = useRef(pageTextEntries);
  entriesRef.current = pageTextEntries;
  const [layoutTemplates, setLayoutTemplates] = useState([]);
  const [selectedLayoutTemplateId, setSelectedLayoutTemplateId] = useState('');
  const [loadingLayoutTemplates, setLoadingLayoutTemplates] = useState(false);
  const [layoutTemplatesError, setLayoutTemplatesError] = useState('');
  const [savingLayoutTemplate, setSavingLayoutTemplate] = useState(false);
  const [copyingLayoutTemplate, setCopyingLayoutTemplate] = useState(false);
  useEffect(() => {
    if (!pageId || activeWorkflowTab !== 'page-layout') return undefined;
    let cancelled = false;
    setLoadingLayoutTemplates(true); setLayoutTemplatesError('');
    getContentLayouts(undefined, pageId).then((response) => {
      if (!cancelled) {
        const templates = response.data.content_layouts || [];
        setLayoutTemplates(templates);
        setSelectedLayoutTemplateId((current) => templates.some((template) => template._id === current) ? current : '');
      }
    }).catch(() => { if (!cancelled) setLayoutTemplatesError('Could not load templates. Reopen Page layout to try again.'); })
      .finally(() => { if (!cancelled) setLoadingLayoutTemplates(false); });
    return () => { cancelled = true; };
  }, [activeWorkflowTab, pageId]);
  const saveLayoutTemplate = useCallback(async (name) => {
    if (!name.trim() || !pageId || !bookId) return false;
    const items = textItemsRef.current.filter((item) => item.visibleOnCanvas !== false).map(versionBox);
    const slotKeys = new Map();
    items.forEach((item) => { if (item.role !== 'main' && !slotKeys.has(item.sourceTextEntryId)) slotKeys.set(item.sourceTextEntryId, `extra-${slotKeys.size + 1}`); });
    setSavingLayoutTemplate(true);
    try {
      const response = await createContentLayout({
        name: name.trim(), book_id: bookId, source_page_id: pageId,
        text_items: items.map((item) => {
          const entry = pageTextEntries.find((candidate) => candidate.id === item.sourceTextEntryId);
          const { texts, sourceTextEntryId, sourceTextEntryKey, ...arrangement } = item;
          return { ...arrangement, name: item.role === 'main' ? 'Main text' : entry?.label || item.name,
            slotKey: item.role === 'main' ? 'main' : slotKeys.get(item.sourceTextEntryId) };
        }),
      });
      if (currentPageId.current !== pageId) return false;
      setLayoutTemplates((current) => [...current, response.data]);
      setSelectedLayoutTemplateId(response.data._id);
      notify('Layout template saved.'); return true;
    } catch (error) {
      notify(error.response?.data?.error || 'Could not save the layout template.', 'error'); return false;
    } finally { setSavingLayoutTemplate(false); }
  }, [bookId, pageId, notify, pageTextEntries, textItemsRef]);
  const copyLayoutTemplate = useCallback(async () => {
    const template = layoutTemplates.find((item) => item._id === selectedLayoutTemplateId);
    if (!compatibleFormat(template?.format, canvasSize)) { notify('Choose a template with this page’s exact dimensions.', 'error'); return; }
    if (!window.confirm('Replace this page’s layout for all languages with this template? All wording and translations will be kept.')) return;
    setCopyingLayoutTemplate(true);
    try {
      const response = await axios.post(`/api/content-layouts/${selectedLayoutTemplateId}/copy-to/${pageId}`, { language: pageLanguage });
      if (currentPageId.current !== pageId) return;
      const page = response.data.page;
      // Preserve unsaved local wording while adopting the new arrangement.
      const entries = (page.page_text_entries || []).map((entry) => ({ ...entry,
        texts: entriesRef.current.find((local) => local.id === entry.id)?.texts || entry.texts || {},
      }));
      const mainTexts = getCurrentTextValues();
      const items = (page.text_items || []).map((item) => ({ ...item,
        texts: item.role === 'main' ? mainTexts : entries.find((entry) => entry.id === item.sourceTextEntryId)?.texts || {},
      }));
      const localWording = Object.fromEntries(Object.entries(mainTexts).map(([language, text]) => [`text_${language}`, text]));
      setPageData((current) => current?._id === pageId ? { ...current, ...page, ...localWording } : current); setPageTextEntries(entries); textItemsRef.current = items; setTextItems(items);
      setSelectedTextItemId(null); setSelectedAdditionalEntryId('');
      notify('Template copied into this page’s private layout.');
    } catch (error) {
      notify(error.response?.data?.error || 'Could not copy the layout template. Reload before retrying if the connection was lost.', 'error');
    } finally { setCopyingLayoutTemplate(false); }
  }, [canvasSize, layoutTemplates, pageId, pageLanguage, getCurrentTextValues, notify, selectedLayoutTemplateId, setPageData, setPageTextEntries, setSelectedAdditionalEntryId, setSelectedTextItemId, setTextItems, textItemsRef]);
  return {
    layoutTemplates: layoutTemplates.filter((item) => compatibleFormat(item.format, canvasSize)),
    selectedLayoutTemplateId, setSelectedLayoutTemplateId, loadingLayoutTemplates, layoutTemplatesError,
    savingLayoutTemplate, copyingLayoutTemplate, saveLayoutTemplate, copyLayoutTemplate,
  };
}
